/**
 * Archive Feed Block - View Script
 *
 * Intercepts search/filter interactions to fire AJAX requests instead of full
 * page reloads, swapping only the results region. The address bar is kept in
 * sync via pushState so filtered URLs remain bookmarkable.
 *
 * Supports:
 * - Search form with clear button
 * - Taxonomy term select (insight/event feeds)
 * - Sort-by dropdown (insight/event feeds)
 * - Upcoming/Past time filter (event feeds)
 * - A-Z letter-group filter (partner/member org/user feeds)
 * - Detail modal with AJAX content loading (partner/member org/user feeds)
 *
 * Graceful degradation: standard <form> GET submission works without JS.
 * Pagination uses standard page reloads and is not intercepted.
 * @package
 */

// Durations must match .is-fading / .is-appearing CSS transition lengths.
const FADE_DURATION_MS = 200;
const APPEAR_DURATION_MS = 300;

// Focusable elements selector used by the focus trap.
const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(', ');

// ============================================================================
// URL builder
// ============================================================================

/**
 * Build a new URL from the current location, replacing all active filter
 * params and stripping any /page/N/ path segment so the address bar always
 * reflects page 1 when a filter changes.
 * @param {object} filters - Active filter values.
 * @param {string} filters.searchTerm
 * @param {string} filters.termSlug
 * @param {string} filters.sortBy
 * @param {string} filters.timeFilter
 * @param {string} filters.azFilter
 * @param filters.activeTerms
 * @param filters.orgId
 * @returns {string} Updated URL string.
 */
const buildUrl = ({ searchTerm, activeTerms, sortBy, timeFilter, azFilter, orgId }) => {
	const url = new URL(window.location.href);
	const params = url.searchParams;

	// Strip /page/N/ so a filter change resets to page 1.
	url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, '/');

	/**
	 *
	 * @param key
	 * @param value
	 */
	const setOrDelete = (key, value) => (value && value.trim() ? params.set(key, value.trim()) : params.delete(key));

	setOrDelete('filter_search', searchTerm);
	setOrDelete('filter_sort', sortBy);
	setOrDelete('filter_time', timeFilter);
	setOrDelete('filter_az', azFilter);
	setOrDelete('filter_org', orgId);

	// Write one URL param per taxonomy filter.
	if (activeTerms && typeof activeTerms === 'object') {
		Object.entries(activeTerms).forEach(([tax, slug]) => {
			setOrDelete(`filter_term_${tax}`, slug);
		});
	}

	return url.toString();
};

// ============================================================================
// Focus trap
// ============================================================================

/**
 * Create a focus trap within an element. Returns a cleanup function.
 * @param {HTMLElement} el - The element to trap focus within.
 * @returns {Function} Cleanup function that removes the event listener.
 */
const createFocusTrap = el => {
	/**
	 *
	 * @param e
	 */
	const handler = e => {
		if (e.key !== 'Tab') return;

		const nodes = [...el.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
			n => !n.closest('[hidden]') && n.offsetParent !== null,
		);
		if (!nodes.length) return;

		const first = nodes[0];
		const last = nodes[nodes.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	};

	el.addEventListener('keydown', handler);
	return () => el.removeEventListener('keydown', handler);
};

// ============================================================================
// Feed initialiser
// ============================================================================

/**
 * Initialise all interactive behaviour for a single archive feed element.
 * @param {HTMLElement} feed - The feed root element carrying data attributes.
 */
const initFeed = feed => {
	const { ajaxUrl, nonce, modalNonce } = feed.dataset;
	const postTypes = JSON.parse(feed.dataset.postTypes || '[]');
	const resultsWrap = feed.querySelector('[data-archive-results]');

	if (!ajaxUrl || !nonce || !postTypes.length || !resultsWrap) return;

	// ---- Static data from data attributes ----
	const isStatic = feed.dataset.static === '1';
	const excludedPosts = JSON.parse(feed.dataset.excludedPosts || '[]');
	const order = feed.dataset.order || 'date';
	const pageBase = feed.dataset.pageBase || '';
	const userRoles = feed.dataset.userRoles || '';
	const defaultTermTax = feed.dataset.defaultTermTaxonomy || '';
	const defaultTerm = feed.dataset.defaultTerm || '';

	// ---- Mutable filter state ----
	let activeTerms = JSON.parse(feed.dataset.activeTerms || '{}');
	let activeAz = feed.dataset.filterAz || '';
	let activeTime = feed.dataset.filterTime || '';
	let activeSort = feed.dataset.filterSort || 'date_desc';
	let activeOrg = feed.dataset.filterOrg || '';
	let lastTrigger = null; // card button that last opened the modal

	// ---- Element references ----
	const form = feed.querySelector('[data-archive-search-form]');
	const searchInput = feed.querySelector('[data-archive-search-input]');
	const clearBtn = feed.querySelector('[data-archive-search-clear]');
	const searchSummary = feed.querySelector('[data-archive-search-summary]');
	const termSelects = [...feed.querySelectorAll('[data-archive-term-select]')];
	const orgSelect = feed.querySelector('[data-archive-org-select]');
	const sortSelect = feed.querySelector('[data-archive-sort-select]');
	const azBtns = [...feed.querySelectorAll('[data-archive-az-filter]')];
	const timeBtns = [...feed.querySelectorAll('[data-archive-time-filter]')];
	const modal = feed.querySelector('[data-archive-modal]');
	const modalBody = feed.querySelector('[data-archive-modal-body]');
	const modalClose = feed.querySelector('[data-archive-modal-close]');

	// ---- Skeleton loader ----

	/**
	 *
	 */
	const showSkeleton = () => {
		let cards = '';
		for (let i = 0; i < 3; i++) {
			cards +=
				'<div class="sol-archive-feed__skeleton-card">' +
				'<div class="sol-archive-feed__skeleton-image"></div>' +
				'<div class="sol-archive-feed__skeleton-line sol-archive-feed__skeleton-line--short"></div>' +
				'<div class="sol-archive-feed__skeleton-line"></div>' +
				'<div class="sol-archive-feed__skeleton-line sol-archive-feed__skeleton-line--medium"></div>' +
				'</div>';
		}
		resultsWrap.innerHTML = `<div class="sol-archive-feed__skeleton">${cards}</div>`;
	};

	// ---- UI sync helpers ----

	/**
	 *
	 * @param terms
	 */
	const syncTermSelects = terms => {
		termSelects.forEach(sel => {
			const tax = sel.dataset.archiveTermSelect;
			if (tax && terms[tax] !== undefined) sel.value = terms[tax];
		});
	};
	/**
	 *
	 * @param id
	 */
	const syncOrgSelect = id => {
		if (orgSelect) orgSelect.value = id;
	};

	/**
	 *
	 * @param term
	 */
	const syncClearBtn = term => {
		if (!clearBtn) return;
		clearBtn.classList.toggle('is-hidden', !term || !term.trim());
	};

	/**
	 *
	 * @param term
	 */
	const syncSearchSummary = term => {
		if (!searchSummary) return;
		if (term && term.trim()) {
			searchSummary.textContent = `Showing results for "${term.trim()}"`;
			searchSummary.classList.remove('is-hidden');
		} else {
			searchSummary.textContent = '';
			searchSummary.classList.add('is-hidden');
		}
	};

	/**
	 *
	 * @param az
	 */
	const syncAzBtns = az => {
		azBtns.forEach(btn => {
			const isActive = btn.dataset.archiveAzFilter === az && az !== '';
			btn.classList.toggle('is-active', isActive);
			btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});
	};

	/**
	 *
	 * @param time
	 */
	const syncTimeBtns = time => {
		timeBtns.forEach(btn => {
			const isActive = btn.dataset.archiveTimeFilter === time;
			btn.classList.toggle('is-active', isActive);
			btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});
	};

	// ---- AJAX fetch ----

	/**
	 * Fetch filtered results and swap the results region.
	 * @param {string} searchTerm - Current search input value.
	 */
	const fetchResults = searchTerm => {
		const targetUrl = buildUrl({
			searchTerm,
			activeTerms,
			sortBy: sortSelect ? activeSort : '',
			timeFilter: activeTime,
			azFilter: activeAz,
			orgId: activeOrg,
		});

		resultsWrap.classList.add('is-fading');

		setTimeout(() => {
			showSkeleton();
			resultsWrap.classList.remove('is-fading');
			feed.classList.add('is-loading');

			const body = new FormData();
			body.append('action', 'sol_archive_feed');
			body.append('nonce', nonce);
			body.append('filter_search', searchTerm);
			body.append('active_terms', JSON.stringify(activeTerms));
			body.append('filter_sort', activeSort);
			body.append('filter_time', activeTime);
			body.append('filter_az', activeAz);
			body.append('filter_org', activeOrg);
			body.append('paged', '1');
			body.append('page_base', pageBase);
			body.append('order', order);
			body.append('user_roles', userRoles);
			body.append('default_term_taxonomy', defaultTermTax);
			body.append('default_term', defaultTerm);
			postTypes.forEach(pt => body.append('post_types[]', pt));
			excludedPosts.forEach(id => body.append('excluded_posts[]', id));

			fetch(ajaxUrl, { method: 'POST', body })
				.then(r => {
					if (!r.ok) throw new Error('Network error');
					return r.json();
				})
				.then(data => {
					if (!data.success) throw new Error('AJAX error');

					resultsWrap.classList.add('is-fading');
					setTimeout(() => {
						resultsWrap.innerHTML = data.data.html;
						resultsWrap.classList.remove('is-fading');
						resultsWrap.classList.add('is-appearing');
						setTimeout(() => resultsWrap.classList.remove('is-appearing'), APPEAR_DURATION_MS);
					}, FADE_DURATION_MS);

					window.history.pushState(null, '', targetUrl);

					if (searchInput) searchInput.value = searchTerm;
					syncTermSelects(activeTerms);
					syncOrgSelect(activeOrg);
					syncClearBtn(searchTerm);
					syncSearchSummary(searchTerm);
					syncAzBtns(activeAz);
					syncTimeBtns(activeTime);

					const rect = feed.getBoundingClientRect();
					if (rect.top < 0) feed.scrollIntoView({ behavior: 'smooth', block: 'start' });
				})
				.catch(() => {
					window.location.href = targetUrl;
				})
				.finally(() => feed.classList.remove('is-loading'));
		}, FADE_DURATION_MS);
	};

	// ---- Modal ----

	// AbortController for the in-flight modal fetch. Replaced on every open,
	// aborted on close so a slow response never populates a closed/reopened modal.
	let modalAbortController = null;

	/**
	 * Show a skeleton loader inside the open modal.
	 */
	const showModalSkeleton = () => {
		if (!modalBody) return;
		modalBody.innerHTML =
			'<div class="sol-archive-feed__modal-skeleton">' +
			'<div class="sol-archive-feed__skeleton-image sol-archive-feed__modal-skeleton-avatar"></div>' +
			'<div class="sol-archive-feed__skeleton-line sol-archive-feed__skeleton-line--short"></div>' +
			'<div class="sol-archive-feed__skeleton-line"></div>' +
			'<div class="sol-archive-feed__skeleton-line sol-archive-feed__skeleton-line--medium"></div>' +
			'<div class="sol-archive-feed__skeleton-line"></div>' +
			'</div>';
	};

	/**
	 * Abort any in-flight modal request and clear the body.
	 */
	const resetModal = () => {
		if (modalAbortController) {
			modalAbortController.abort();
			modalAbortController = null;
		}
		if (modalBody) modalBody.innerHTML = '';
	};

	/**
	 * Open the modal and fetch content for the given post or user.
	 * @param {string} postId   - Post ID (for MODAL_POST_TYPES).
	 * @param {string} userId   - User ID (for user feeds).
	 * @param {string} postType - Post type slug.
	 */
	const openModal = (postId, userId, postType) => {
		if (!modal || !modalBody) return;

		// Cancel any previous in-flight request before starting a new one.
		resetModal();

		showModalSkeleton();
		modal.showModal();

		// Store the cleanup fn on the modal element for retrieval on close.
		modal._removeTrap = createFocusTrap(modal);

		// Focus close button immediately so keyboard users have a landing point.
		modalClose?.focus();

		modalAbortController = new AbortController();

		const body = new FormData();
		body.append('action', 'sol_archive_feed_modal');
		body.append('nonce', modalNonce || nonce);
		body.append('post_type', postType || '');
		if (postId) body.append('post_id', postId);
		if (userId) body.append('user_id', userId);

		fetch(ajaxUrl, { method: 'POST', body, signal: modalAbortController.signal })
			.then(r => {
				if (!r.ok) throw new Error('Network error');
				return r.json();
			})
			.then(data => {
				if (!data.success) throw new Error('Modal content error');
				modalBody.innerHTML = data.data.html;

				// Wire up the modal title id so aria-labelledby works after inject.
				const heading = modalBody.querySelector('h2, h3');
				if (heading && modal.getAttribute('aria-labelledby')) {
					heading.id = modal.getAttribute('aria-labelledby');
				}

				// Move focus to first focusable element inside modal body.
				const first = modalBody.querySelector(FOCUSABLE_SELECTOR);
				(first || modalClose)?.focus();
			})
			.catch(err => {
				// Ignore aborted requests — modal was closed intentionally.
				if (err.name === 'AbortError') return;
				if (modalBody) {
					modalBody.innerHTML =
						'<p class="sol-archive-feed__modal-error">' +
						'Sorry, the content could not be loaded. Please try again.' +
						'</p>';
				}
			})
			.finally(() => {
				modalAbortController = null;
			});
	};

	/**
	 * Close the modal and return focus to the card that triggered it.
	 */
	const closeModal = () => {
		if (!modal) return;
		modal.close();
	};

	// modal 'close' event fires for both .close() and native Escape key.
	if (modal) {
		modal.addEventListener('close', () => {
			resetModal();
			if (typeof modal._removeTrap === 'function') {
				modal._removeTrap();
				modal._removeTrap = null;
			}
			lastTrigger?.focus();
		});

		// Close on backdrop click (click on the <dialog> element itself, not its children).
		modal.addEventListener('click', e => {
			if (e.target === modal) closeModal();
		});
	}

	if (modalClose) {
		modalClose.addEventListener('click', closeModal);
	}

	// Delegated click listener on the results region catches cards loaded via AJAX too.
	resultsWrap.addEventListener('click', e => {
		const trigger = e.target.closest('[data-archive-modal-trigger]');
		if (!trigger || !modal) return;

		lastTrigger = trigger;
		openModal(trigger.dataset.postId || '', trigger.dataset.userId || '', trigger.dataset.postType || '');
	});

	// ---- Filter event listeners ----
	// Static feeds (e.g. team_member) have no filter UI and must not fire AJAX or
	// update the URL — multiple feeds can appear on one page and URL state is shared.
	if (!isStatic) {
		// Search form submit.
		if (form && searchInput) {
			form.addEventListener('submit', e => {
				e.preventDefault();
				fetchResults(searchInput.value);
			});
		}

		// Clear button.
		if (clearBtn) {
			clearBtn.addEventListener('click', () => {
				if (searchInput) searchInput.value = '';
				fetchResults('');
			});
		}

		// Taxonomy term selects — one per configured taxonomy.
		termSelects.forEach(sel => {
			sel.addEventListener('change', () => {
				const tax = sel.dataset.archiveTermSelect;
				if (tax) activeTerms = { ...activeTerms, [tax]: sel.value };
				fetchResults(searchInput ? searchInput.value : '');
			});
		});

		// Organisation select.
		if (orgSelect) {
			orgSelect.addEventListener('change', () => {
				activeOrg = orgSelect.value;
				fetchResults(searchInput ? searchInput.value : '');
			});
		}

		// Sync all filter controls to match prefiltered URL state on page load.
		syncTermSelects(activeTerms);
		syncOrgSelect(activeOrg);
		syncAzBtns(activeAz);
		syncTimeBtns(activeTime);

		// Sort-by select.
		if (sortSelect) {
			sortSelect.value = activeSort;
			sortSelect.addEventListener('change', () => {
				activeSort = sortSelect.value;
				fetchResults(searchInput ? searchInput.value : '');
			});
		}

		// Event time filter buttons.
		timeBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				activeTime = btn.dataset.archiveTimeFilter;
				syncTimeBtns(activeTime);
				fetchResults(searchInput ? searchInput.value : '');
			});
		});

		// A-Z letter-group buttons (toggle off on second click).
		azBtns.forEach(btn => {
			btn.addEventListener('click', () => {
				const group = btn.dataset.archiveAzFilter;
				activeAz = activeAz === group ? '' : group;
				syncAzBtns(activeAz);
				fetchResults(searchInput ? searchInput.value : '');
			});
		});

		// Reset button — clears all filter state and re-fetches.
		if (form) {
			form.addEventListener('reset', e => {
				e.preventDefault();
				if (searchInput) searchInput.value = '';
				activeTerms = {};
				activeAz = '';
				activeTime = 'upcoming';
				activeSort = '';
				activeOrg = '';
				termSelects.forEach(sel => {
					sel.value = '';
				});
				if (orgSelect) orgSelect.value = '';
				if (sortSelect) sortSelect.selectedIndex = 0;
				syncAzBtns('');
				syncTimeBtns('upcoming');
				syncClearBtn('');
				syncSearchSummary('');
				fetchResults('');
			});
		}
	} // end !isStatic
};

// ============================================================================
// Boot
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-archive-feed]').forEach(initFeed);
});
